import {
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysLocaleMessages,
} from '../../provider/context';
import type { BaseMultiSelectOption, BaseMultiSelectProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

const typeaheadResetDelay = 500;

function validateMultiSelectProps({
  options,
  value,
  defaultValue,
  invalid,
  disabled,
  required,
  openForPreview,
  maxTagCount,
}: Pick<
  BaseMultiSelectProps,
  | 'defaultValue'
  | 'disabled'
  | 'invalid'
  | 'maxTagCount'
  | 'openForPreview'
  | 'options'
  | 'required'
  | 'value'
>) {
  for (const [name, state] of [
    ['invalid', invalid],
    ['disabled', disabled],
    ['required', required],
    ['openForPreview', openForPreview],
  ] as const) {
    if (typeof state !== 'boolean') {
      throw new TypeError(`BaseMultiSelect ${name} must be a boolean.`);
    }
  }
  if (maxTagCount !== undefined && typeof maxTagCount !== 'number') {
    throw new TypeError('BaseMultiSelect maxTagCount must be a number.');
  }
  if (options !== undefined && !Array.isArray(options)) {
    throw new TypeError('BaseMultiSelect options must be an array.');
  }
  options?.forEach((option, index) => {
    if (!option || typeof option !== 'object') {
      throw new TypeError(
        `BaseMultiSelect option at index ${index} must be an object.`,
      );
    }
    if (typeof option.value !== 'string') {
      throw new TypeError(
        `BaseMultiSelect option value at index ${index} must be a string.`,
      );
    }
    if (
      option.disabled !== undefined &&
      typeof option.disabled !== 'boolean'
    ) {
      throw new TypeError(
        `BaseMultiSelect option disabled at index ${index} must be a boolean.`,
      );
    }
    if (
      option.textValue !== undefined &&
      typeof option.textValue !== 'string'
    ) {
      throw new TypeError(
        `BaseMultiSelect option textValue at index ${index} must be a string.`,
      );
    }
  });

  for (const [name, values] of [
    ['value', value],
    ['defaultValue', defaultValue],
  ] as const) {
    if (values !== undefined && !Array.isArray(values)) {
      throw new TypeError(`BaseMultiSelect ${name} must be an array.`);
    }
    values?.forEach((item, index) => {
      if (typeof item !== 'string') {
        throw new TypeError(
          `BaseMultiSelect ${name} item at index ${index} must be a string.`,
        );
      }
    });
  }
}

function normalizeValues(values: readonly string[] | undefined) {
  const nextValues: string[] = [];
  const seen = new Set<string>();

  values?.forEach((value) => {
    if (seen.has(value)) return;
    seen.add(value);
    nextValues.push(value);
  });

  return nextValues;
}

function getNextEnabledIndex(
  options: BaseMultiSelectOption[],
  currentIndex: number,
  direction: 1 | -1,
) {
  if (!options.length) return -1;
  const startIndex = currentIndex >= 0 ? currentIndex : direction === 1 ? -1 : 0;

  for (let offset = 1; offset <= options.length; offset += 1) {
    const nextIndex =
      (startIndex + offset * direction + options.length) % options.length;
    if (!options[nextIndex]?.disabled) return nextIndex;
  }

  return -1;
}

function getBoundaryEnabledIndex(
  options: BaseMultiSelectOption[],
  boundary: 'first' | 'last',
) {
  if (boundary === 'first') {
    return options.findIndex((option) => !option.disabled);
  }
  for (let index = options.length - 1; index >= 0; index -= 1) {
    if (!options[index]?.disabled) return index;
  }
  return -1;
}

function getSafeTagCount(maxTagCount: number | undefined, total: number) {
  if (maxTagCount === undefined || !Number.isFinite(maxTagCount)) return total;
  return Math.max(0, Math.min(total, Math.floor(maxTagCount)));
}

function getOptionText(option: BaseMultiSelectOption) {
  if (option.textValue !== undefined) return option.textValue;
  if (typeof option.label === 'string') return option.label;
  return option.value;
}

function toDomId(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, '-');
}

export const BaseMultiSelect = forwardRef<HTMLDivElement, BaseMultiSelectProps>(
  function BaseMultiSelect(
    {
      options,
      value,
      defaultValue,
      name,
      placeholder,
      icon,
      invalid = false,
      disabled = false,
      required = false,
      openForPreview = false,
      maxTagCount,
      removeLabel,
      emptyLabel,
      appearance,
      className,
      classNames,
      style,
      id,
      'aria-invalid': nativeAriaInvalid,
      'aria-label': nativeAriaLabel,
      'aria-labelledby': nativeAriaLabelledBy,
      'aria-describedby': nativeAriaDescribedBy,
      'aria-errormessage': nativeAriaErrorMessage,
      onValueChange,
      ...rootProps
    },
    ref,
  ) {
    validateMultiSelectProps({
      options,
      value,
      defaultValue,
      invalid,
      disabled,
      required,
      openForPreview,
      maxTagCount,
    });

    const messages = useMonkeysLocaleMessages().multiSelect;
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const generatedId = useId();
    const multiSelectId = id ?? generatedId;
    const controlId = `${multiSelectId}-control`;
    const listboxId = `${multiSelectId}-listbox`;
    const rootRef = useRef<HTMLDivElement | null>(null);
    const controlRef = useRef<HTMLDivElement | null>(null);
    const composingRef = useRef(false);
    const typeaheadBufferRef = useRef('');
    const typeaheadTimerRef = useRef<{
      ownerWindow: Window;
      timerId: number;
    } | null>(null);
    const optionList = options ?? [];
    const optionIds = optionList.map(
      (option, index) =>
        `${multiSelectId}-option-${index}-${toDomId(option.value)}`,
    );
    const forceOpen = openForPreview;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const open = forceOpen || uncontrolledOpen;
    const initialDefaultValueRef = useRef(normalizeValues(defaultValue));
    const [uncontrolledValue, setUncontrolledValue] = useState(() => [
      ...initialDefaultValueRef.current,
    ]);
    const [activeIndex, setActiveIndex] = useState(() =>
      getBoundaryEnabledIndex(optionList, 'first'),
    );
    const isControlled = value !== undefined;
    const selectedValues = useMemo(
      () => normalizeValues(isControlled ? value : uncontrolledValue),
      [isControlled, uncontrolledValue, value],
    );
    const selectedValueSet = useMemo(
      () => new Set(selectedValues),
      [selectedValues],
    );
    const optionByValue = useMemo(() => {
      const map = new Map<string, BaseMultiSelectOption>();
      optionList.forEach((option) => {
        if (!map.has(option.value)) map.set(option.value, option);
      });
      return map;
    }, [optionList]);
    const selectedOptions = useMemo(
      () =>
        selectedValues
          .map((selectedValue) => optionByValue.get(selectedValue))
          .filter(
            (option): option is BaseMultiSelectOption => option !== undefined,
          ),
      [optionByValue, selectedValues],
    );
    const firstEnabledIndex = getBoundaryEnabledIndex(optionList, 'first');
    const resolvedActiveIndex =
      activeIndex >= 0 && !optionList[activeIndex]?.disabled
        ? activeIndex
        : firstEnabledIndex;
    const visibleTagCount = getSafeTagCount(
      maxTagCount,
      selectedOptions.length,
    );
    const visibleOptions = selectedOptions.slice(0, visibleTagCount);
    const overflowCount = selectedOptions.length - visibleOptions.length;
    const isPlaceholder = selectedOptions.length === 0;
    const layerDirection = rootProps.dir ?? componentAttributes.dir;
    const rootState = disabled
      ? 'disabled'
      : invalid
        ? 'invalid'
        : open
          ? 'open'
          : 'closed';

    const setRootRef = (node: HTMLDivElement | null) => {
      rootRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };

    const setOpen = (nextOpen: boolean) => {
      if (disabled || forceOpen) return;
      setUncontrolledOpen(nextOpen);
      if (nextOpen && resolvedActiveIndex < 0) {
        setActiveIndex(firstEnabledIndex);
      }
    };

    const commitValues = (nextValues: string[]) => {
      const normalizedValues = normalizeValues(nextValues);
      if (!isControlled) setUncontrolledValue(normalizedValues);
      onValueChange?.(normalizedValues);
    };

    const toggleOption = (option: BaseMultiSelectOption | undefined) => {
      if (!option || option.disabled || disabled) return;
      const nextValues = selectedValueSet.has(option.value)
        ? selectedValues.filter((selectedValue) => selectedValue !== option.value)
        : [...selectedValues, option.value];
      commitValues(nextValues);
    };

    const removeOption = (option: BaseMultiSelectOption) => {
      if (disabled) return;
      commitValues(
        selectedValues.filter((selectedValue) => selectedValue !== option.value),
      );
    };

    const moveActive = (nextIndex: number) => {
      if (nextIndex < 0 || optionList[nextIndex]?.disabled) return;
      setActiveIndex(nextIndex);
      if (!open) setOpen(true);
    };

    const handleTypeahead = (
      event: KeyboardEvent<HTMLDivElement>,
      character: string,
    ) => {
      const ownerWindow = event.currentTarget.ownerDocument.defaultView;
      if (!ownerWindow) return;
      const nextBuffer = `${typeaheadBufferRef.current}${character}`.toLocaleLowerCase();
      typeaheadBufferRef.current = nextBuffer;
      if (typeaheadTimerRef.current) {
        typeaheadTimerRef.current.ownerWindow.clearTimeout(
          typeaheadTimerRef.current.timerId,
        );
      }
      typeaheadTimerRef.current = {
        ownerWindow,
        timerId: ownerWindow.setTimeout(() => {
          typeaheadBufferRef.current = '';
          typeaheadTimerRef.current = null;
        }, typeaheadResetDelay),
      };
      const repeatedCharacter = Array.from(nextBuffer).every(
        (bufferCharacter) => bufferCharacter === nextBuffer[0],
      );
      const query = repeatedCharacter ? character.toLocaleLowerCase() : nextBuffer;
      for (let offset = 1; offset <= optionList.length; offset += 1) {
        const index =
          (resolvedActiveIndex + offset + optionList.length) % optionList.length;
        const option = optionList[index];
        if (
          !option?.disabled &&
          getOptionText(option).trim().toLocaleLowerCase().startsWith(query)
        ) {
          event.preventDefault();
          moveActive(index);
          return;
        }
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (disabled || composingRef.current || event.nativeEvent.isComposing) {
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!open) {
          setActiveIndex(firstEnabledIndex);
          setOpen(true);
        } else {
          moveActive(
            getNextEnabledIndex(
              optionList,
              resolvedActiveIndex,
              event.key === 'ArrowDown' ? 1 : -1,
            ),
          );
        }
        return;
      }

      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        moveActive(
          getBoundaryEnabledIndex(
            optionList,
            event.key === 'Home' ? 'first' : 'last',
          ),
        );
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (!open) setOpen(true);
        else toggleOption(optionList[resolvedActiveIndex]);
        return;
      }

      if (event.key === 'Escape' && open) {
        event.preventDefault();
        setOpen(false);
        return;
      }

      if (event.key === 'Tab' && open) {
        setOpen(false);
        return;
      }

      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !/\s/.test(event.key)
      ) {
        handleTypeahead(event, event.key);
      }
    };

    useEffect(() => {
      if (!open || forceOpen) return undefined;
      const ownerDocument = rootRef.current?.ownerDocument;
      if (!ownerDocument) return undefined;
      const handlePointerDown = (event: PointerEvent) => {
        if (!rootRef.current?.contains(event.target as Node)) {
          setUncontrolledOpen(false);
        }
      };
      ownerDocument.addEventListener('pointerdown', handlePointerDown);
      return () => ownerDocument.removeEventListener('pointerdown', handlePointerDown);
    }, [forceOpen, open]);

    useEffect(() => {
      if (isControlled) return undefined;
      const form = rootRef.current?.closest('form');
      if (!form) return undefined;
      const handleReset = () => {
        setUncontrolledValue([...initialDefaultValueRef.current]);
        setActiveIndex(getBoundaryEnabledIndex(optionList, 'first'));
        if (!forceOpen) setUncontrolledOpen(false);
      };
      form.addEventListener('reset', handleReset);
      return () => form.removeEventListener('reset', handleReset);
    }, [forceOpen, isControlled, optionList]);

    useEffect(
      () => () => {
        if (typeaheadTimerRef.current) {
          typeaheadTimerRef.current.ownerWindow.clearTimeout(
            typeaheadTimerRef.current.timerId,
          );
        }
      },
      [],
    );

    return (
      <div
        {...componentAttributes}
        {...rootProps}
        id={id}
        ref={setRootRef}
        dir={layerDirection}
        data-monkeys-component="multi-select"
        data-slot="root"
        data-state={rootState}
        data-disabled={disabled || undefined}
        data-invalid={invalid || undefined}
        className={cn(
          theme.slots.controlWrapper,
          'overflow-visible',
          open && 'z-50',
          invalid && theme.slots.controlInvalid,
          disabled && theme.slots.controlDisabled,
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.control,
          ...(invalid ? theme.styles.controlInvalid : undefined),
          ...(disabled ? theme.styles.controlDisabled : undefined),
          ...style,
        }}
      >
        {hasRenderableNode(icon) ? (
          <span className={cn(theme.slots.controlIcon, classNames?.icon)}>
            {icon}
          </span>
        ) : null}
        <div
          {...componentAttributes}
          ref={controlRef}
          id={controlId}
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          dir={layerDirection}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={
            open && resolvedActiveIndex >= 0
              ? optionIds[resolvedActiveIndex]
              : undefined
          }
          aria-autocomplete="none"
          aria-disabled={disabled || undefined}
          aria-required={required || undefined}
          aria-invalid={nativeAriaInvalid ?? (invalid || undefined)}
          aria-label={nativeAriaLabel}
          aria-labelledby={nativeAriaLabelledBy}
          aria-describedby={nativeAriaDescribedBy}
          aria-errormessage={nativeAriaErrorMessage}
          data-monkeys-component="multi-select"
          data-slot="control"
          data-state={rootState}
          className={cn(
            theme.slots.controlBase,
            'flex min-h-9 cursor-pointer flex-wrap items-center gap-1.5 py-1.5 pr-9 text-left',
            disabled && '!cursor-not-allowed',
            classNames?.control,
          )}
          onMouseDown={(event) => {
            if (disabled || event.button !== 0) return;
            event.preventDefault();
            controlRef.current?.focus();
          }}
          onClick={() => {
            controlRef.current?.focus();
            setOpen(!open);
          }}
          onBlur={(event) => {
            if (!rootRef.current?.contains(event.relatedTarget)) setOpen(false);
          }}
          onCompositionStart={() => {
            composingRef.current = true;
          }}
          onCompositionEnd={() => {
            composingRef.current = false;
          }}
          onKeyDown={handleKeyDown}
        >
          <span
            data-monkeys-component="multi-select"
            data-slot="tag-list"
            data-state={isPlaceholder ? 'empty' : 'filled'}
            className={cn(
              'flex min-w-0 flex-1 flex-wrap items-center gap-1.5',
              classNames?.tagList,
            )}
          >
            {visibleOptions.map((option, index) => (
              <span
                key={`${option.value}-${index}`}
                data-monkeys-component="multi-select"
                data-slot="tag"
                data-state="selected"
                className={cn(
                  'inline-flex max-w-full items-center gap-1 rounded-full border border-border/70 bg-muted/65 px-2 py-0.5 text-xs font-medium text-foreground',
                  classNames?.tag,
                )}
              >
                <span
                  data-slot="tag-label"
                  className={cn('max-w-40 truncate', classNames?.tagLabel)}
                >
                  {option.label}
                </span>
                <button
                  {...componentAttributes}
                  type="button"
                  tabIndex={-1}
                  dir={layerDirection}
                  aria-label={
                    removeLabel?.(option) ??
                    messages.removeOption({ value: option.value })
                  }
                  disabled={disabled}
                  data-monkeys-component="multi-select"
                  data-slot="tag-remove"
                  data-state="selected"
                  className={cn(
                    'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-background/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:!cursor-not-allowed',
                    classNames?.tagRemove,
                  )}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    removeOption(option);
                    controlRef.current?.focus();
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <span aria-hidden="true">x</span>
                </button>
              </span>
            ))}
            {overflowCount > 0 ? (
              <span
                data-monkeys-component="multi-select"
                data-slot="tag-overflow"
                data-state="selected"
                className={cn(
                  'inline-flex max-w-full items-center rounded-full border border-border/70 bg-muted/45 px-2 py-0.5 text-xs font-medium text-muted-foreground',
                  classNames?.tag,
                )}
              >
                +{overflowCount}
              </span>
            ) : null}
            {isPlaceholder ? (
              <span
                data-monkeys-component="multi-select"
                data-slot="placeholder"
                data-state="empty"
                className={cn(
                  'min-w-0 truncate',
                  theme.slots.selectPlaceholder,
                  classNames?.placeholder,
                )}
              >
                {placeholder === undefined ? messages.placeholder : placeholder}
              </span>
            ) : null}
          </span>
        </div>
        <span
          data-monkeys-component="multi-select"
          data-slot="chevron"
          data-state={open ? 'open' : 'closed'}
          className={cn(
            theme.slots.selectChevron,
            open && 'rotate-[225deg]',
            classNames?.chevron,
          )}
        />
        {name
          ? selectedValues.map((selectedValue) => (
              <input
                {...componentAttributes}
                key={selectedValue}
                type="hidden"
                name={name}
                value={selectedValue}
                disabled={disabled}
                dir={layerDirection}
                data-monkeys-component="multi-select"
                data-slot="hidden-input"
                data-state="selected"
                className={classNames?.hiddenInput}
              />
            ))
          : null}
        {open ? (
          <div
            {...componentAttributes}
            id={listboxId}
            role="listbox"
            dir={layerDirection}
            aria-multiselectable="true"
            aria-labelledby={controlId}
            data-monkeys-component="multi-select"
            data-slot="menu"
            data-state="open"
            className={cn(theme.slots.selectMenu, classNames?.menu)}
            style={theme.styles.selectMenu}
          >
            {optionList.length > 0 ? (
              optionList.map((option, index) => {
                const selected = selectedValueSet.has(option.value);
                const active = index === resolvedActiveIndex;
                const optionState = option.disabled
                  ? 'disabled'
                  : active
                    ? 'active'
                    : selected
                      ? 'selected'
                      : 'idle';
                return (
                  <button
                    {...componentAttributes}
                    key={`${option.value}-${index}`}
                    id={optionIds[index]}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    dir={layerDirection}
                    aria-selected={selected}
                    disabled={option.disabled}
                    data-monkeys-component="multi-select"
                    data-slot="option"
                    data-state={optionState}
                    data-active={active || undefined}
                    data-selected={selected || undefined}
                    data-disabled={option.disabled || undefined}
                    className={cn(
                      theme.slots.selectOption,
                      'gap-2',
                      active && theme.slots.selectOptionActive,
                      selected && theme.slots.selectOptionSelected,
                      option.disabled && theme.slots.selectOptionDisabled,
                      classNames?.option,
                    )}
                    style={{
                      ...theme.styles.selectOption,
                      ...(active ? theme.styles.selectOptionActive : undefined),
                      ...(selected
                        ? theme.styles.selectOptionSelected
                        : undefined),
                    }}
                    onMouseEnter={() => {
                      if (!option.disabled) setActiveIndex(index);
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      toggleOption(option);
                      controlRef.current?.focus();
                    }}
                  >
                    <span
                      aria-hidden="true"
                      data-slot="option-indicator"
                      className={cn(
                        'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-border/70 text-[10px]',
                        selected &&
                          'border-primary/40 bg-primary text-primary-foreground',
                        classNames?.optionIndicator,
                      )}
                    >
                      {selected ? '✓' : null}
                    </span>
                    <span
                      data-slot="option-label"
                      className={cn(
                        'min-w-0 flex-1 truncate',
                        classNames?.optionLabel,
                      )}
                    >
                      {option.label}
                    </span>
                  </button>
                );
              })
            ) : (
              <div
                {...componentAttributes}
                dir={layerDirection}
                role="option"
                tabIndex={-1}
                aria-disabled="true"
                aria-selected="false"
                data-monkeys-component="multi-select"
                data-slot="empty"
                data-state="empty"
                className={cn(
                  'px-2.5 py-2 text-sm text-muted-foreground',
                  classNames?.empty,
                )}
              >
                {emptyLabel === undefined ? messages.empty : emptyLabel}
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  },
);
